import React, { useEffect, useState } from "react";
import { useBlog } from "./useHook";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useNavigate, useParams } from "react-router-dom";
import { IoEye } from "react-icons/io5";
import { MdDelete } from "react-icons/md";
import {
  FiEdit2,
  FiFilter,
  FiPlus,
  FiRotateCcw,
  FiSearch,
} from "react-icons/fi";
import Swal from "sweetalert2";
import GlobalPagination from "../../Components/Pagination/GlobalPagination";
import RecordPosition from "../../Components/Modals/RecordPosition/RecordPosition";
import "./style.css";
import { DeleteIcon } from "lucide-react";

const normalizeImageUrl = (url) => {
  if (!url || typeof url !== "string") return url;
  if (url.startsWith("blob:") || url.startsWith("data:")) return url;
  let normalized = url.replace(/\/api(?=\/)/, "");
  if (
    typeof window !== "undefined" &&
    window.location?.protocol === "https:" &&
    normalized.startsWith("http://")
  ) {
    normalized = "https:" + normalized.slice(5);
  }
  try {
    return encodeURI(decodeURI(normalized));
  } catch {
    return normalized;
  }
};

const Blog = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    getBlogCategories,
    postBlog,
    updateBlog,
    getBlogs,
    getBlogById,
    deleteBlog,
    searchBlogs,
    updateBlogPosition,
  } = useBlog();
  const [blogCategories, setBlogCategories] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingBlogId, setEditingBlogId] = useState(null);
  const [isLoadingBlog, setIsLoadingBlog] = useState(false);
  const [nameQuery, setNameQuery] = useState("");
  const [categoryNameQuery, setCategoryNameQuery] = useState("");
  const [sortBy, setSortBy] = useState(""); // newest, oldest, popular
  const [formData, setFormData] = useState({
    categoryId: "",
    name: "",
    status: "draft",
    description: "",
    headerImage: null,
    customUrl: "",
  });
  const [isFormValid, setIsFormValid] = useState(false);
  const [isPositionModalOpen, setIsPositionModalOpen] = useState(false);
  const [selectedBlogForPosition, setSelectedBlogForPosition] = useState(null);
  const [pagination, setPagination] = useState({
    totalCount: 0,
    totalPages: 0,
    currentPage: 1,
    limit: 10,
    offset: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });
  const [contentBlocks, setContentBlocks] = useState([
    { text: "", textAlignment: "left", image: null, imageAlignment: "right" },
  ]);
  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ script: "sub" }, { script: "super" }],
      [{ indent: "-1" }, { indent: "+1" }],
      [{ direction: "rtl" }],
      [{ color: [] }, { background: [] }],
      [{ font: [] }],
      [{ align: [] }],
      ["blockquote", "code-block"],
      ["link"],
      ["clean"],
    ],
  };

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
    "script",
    "indent",
    "direction",
    "color",
    "background",
    "font",
    "align",
    "blockquote",
    "code-block",
    "link",
  ];

  useEffect(() => {
    const fetchBlogCategories = async () => {
      try {
        const categories = await getBlogCategories();
        // Ensure categories is always an array
        if (Array.isArray(categories)) {
          setBlogCategories(categories);
        } else if (categories && Array.isArray(categories.data)) {
          setBlogCategories(categories.data);
        } else {
          setBlogCategories([]);
        }
      } catch (error) {
        console.error("Error fetching blog categories:", error);
        setBlogCategories([]);
      }
    };
    fetchBlogCategories();
  }, []);
  const loadData = async () => {
    try {
      const searchParams = {};

      if (nameQuery && nameQuery.trim()) {
        searchParams.name = nameQuery.trim();
      }

      if (categoryNameQuery && categoryNameQuery.trim()) {
        searchParams.categoryName = categoryNameQuery.trim();
      }

      if (sortBy) {
        if (sortBy === "newest") {
          searchParams.newest = true;
        } else if (sortBy === "oldest") {
          searchParams.oldest = true;
        } else if (sortBy === "popular") {
          searchParams.popular = true;
        }
      }

      const response = await getBlogs(
        {
          offset: pagination.offset,
          limit: pagination.limit,
        },
        searchParams,
      );
      setBlogs(response.data);
      if (response.pagination) {
        setPagination(response.pagination);
      }
    } catch (error) {
      console.error("Error fetching blogs:", error);
    }
  };

  useEffect(() => {
    loadData();
  }, [pagination.offset, pagination.limit]);

  const handlePageChange = (newOffset) => {
    setPagination((prev) => ({
      ...prev,
      offset: newOffset,
    }));
  };

  const handleSearch = async () => {
    try {
      const searchParams = {};

      if (nameQuery && nameQuery.trim()) {
        searchParams.name = nameQuery.trim();
      }

      if (categoryNameQuery && categoryNameQuery.trim()) {
        searchParams.categoryName = categoryNameQuery.trim();
      }

      if (sortBy) {
        if (sortBy === "newest") {
          searchParams.newest = true;
        } else if (sortBy === "oldest") {
          searchParams.oldest = true;
        } else if (sortBy === "popular") {
          searchParams.popular = true;
        }
      }

      setPagination((prev) => ({
        ...prev,
        offset: 0,
      }));

      const response = await searchBlogs(
        {
          offset: 0,
          limit: pagination.limit,
        },
        searchParams,
      );

      setBlogs(response.data);
      if (response.pagination) {
        setPagination(response.pagination);
      }
    } catch (error) {
      console.error("Error in search:", error);
      Swal.fire({
        title: "Error",
        text: "There was an issue with the search.",
        icon: "error",
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    }
  };

  const handleReset = async () => {
    setNameQuery("");
    setCategoryNameQuery("");
    setSortBy("");
    setPagination((prev) => ({
      ...prev,
      offset: 0,
    }));
    try {
      const response = await getBlogs(
        {
          offset: 0,
          limit: pagination.limit,
        },
        {},
      );
      setBlogs(response.data);
      if (response.pagination) {
        setPagination(response.pagination);
      }
    } catch (error) {
      console.error("Error fetching blogs:", error);
    }
  };

  useEffect(() => {
    if (id) {
      setIsEditMode(true);
      setEditingBlogId(id);
      loadBlogForEdit(id);
    }
  }, [id]);

  const loadBlogForEdit = async (blogId) => {
    setIsLoadingBlog(true);
    try {
      const blogData = await getBlogById(blogId);

      setFormData({
        categoryId: blogData.categoryId || blogData.category?.id || "",
        name: blogData.name || "",
        status: blogData.status || "draft",
        description: blogData.description || "",
        headerImage: blogData.headerImage
          ? {
            preview: blogData.headerImage,
            file: null,
          }
          : null,
        customUrl: blogData.customUrl || "",
      });

      if (blogData.content && Array.isArray(blogData.content)) {
        const sections = [];
        let currentSection = { text: "", image: null };

        blogData.content.forEach((block) => {
          if (block.type === "text") {
            if (currentSection.text !== "" || currentSection.image !== null) {
              sections.push(currentSection);
              currentSection = { text: block.content, textAlignment: block.alignment || "left", image: null, imageAlignment: "right" };
            } else {
              currentSection.text = block.content;
              currentSection.textAlignment = block.alignment || "left";
            }
          } else if (block.type === "image") {
            currentSection.image = block.content;
            currentSection.imageAlignment = block.alignment || "right";
            sections.push(currentSection);
            currentSection = { text: "", textAlignment: "left", image: null, imageAlignment: "right" };
          }
        });

        if (currentSection.text !== "" || currentSection.image !== null) {
          sections.push(currentSection);
        }

        setContentBlocks(sections.length > 0 ? sections : [{ text: "", textAlignment: "left", image: null, imageAlignment: "right" }]);
      } else {
        setContentBlocks([{ text: "", textAlignment: "left", image: null, imageAlignment: "right" }]);
      }

      setIsModalOpen(true);
    } catch (error) {
      console.error("Error loading blog for edit:", error);
      Swal.fire({
        title: "Error!",
        text: "Failed to load blog data for editing.",
        icon: "error",
      });
      navigate("/blogs");
    } finally {
      setIsLoadingBlog(false);
    }
  };

  const addContentBlock = (index, e) => {
    e.preventDefault();
    const newBlocks = [...contentBlocks];
    newBlocks.splice(index + 1, 0, { text: "", textAlignment: "left", image: null, imageAlignment: "right" });
    setContentBlocks(newBlocks);
  };

  const handleViewBlog = (blogId) => {
    navigate(`/blog/view/${blogId}`);
  };

  const handlePositionClick = (blog) => {
    setSelectedBlogForPosition({
      recordId: blog.id,
      currentPosition: blog.position ?? 0,
    });
    setIsPositionModalOpen(true);
  };

  const handleUpdatePosition = async (recordId, newPosition) => {
    await updateBlogPosition(recordId, newPosition);
    loadData();
  };

  const closePositionModal = () => {
    setIsPositionModalOpen(false);
    setSelectedBlogForPosition(null);
  };

  const handleEditBlog = (blogId) => {
    navigate(`/blogs/edit/${blogId}`);
  };

  const handleDeleteBlog = async (blogId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteBlog(blogId);
          loadData();
          Swal.fire({
            title: "Deleted!",
            text: "Blog has been deleted successfully.",
            icon: "success",
            timer: 2000,
            showConfirmButton: false,
          });
        } catch (error) {
          console.error("Error deleting blog:", error);
          Swal.fire({
            title: "Error!",
            text: "Failed to delete blog.",
            icon: "error",
          });
        }
      }
    });
  };


  const removeBlock = (index, e) => {
    e.preventDefault();
    if (contentBlocks.length > 1) {
      const newBlocks = [...contentBlocks];
      newBlocks.splice(index, 1);
      setContentBlocks(newBlocks);
    }
  };

  const handleTextChange = (index, value) => {
    const newBlocks = [...contentBlocks];
    newBlocks[index].text = value;
    setContentBlocks(newBlocks);
  };

  const handleAlignmentChange = (index, field, value) => {
    const newBlocks = [...contentBlocks];
    newBlocks[index][field] = value;
    setContentBlocks(newBlocks);
  };

  const handleImageUpload = (index, event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const newBlocks = [...contentBlocks];
        newBlocks[index].image = reader.result;
        setContentBlocks(newBlocks);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleHeaderImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const checkImageResolution = (file) => {
        return new Promise((resolve) => {
          const img = new Image();
          img.onload = () => {
            if (img.width === 1600 && img.height === 900) {
              resolve(true);
            } else {
              resolve(false);
            }
          };
          img.src = URL.createObjectURL(file);
        });
      };

      const isValid = await checkImageResolution(file);
      if (isValid) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFormData((prev) => {
            const newFormData = {
              ...prev,
              headerImage: {
                preview: reader.result,
                file: file,
              },
            };
            return newFormData;
          });
        };
        reader.readAsDataURL(file);
      } else {
        Swal.fire({
          title: "Invalid Image Resolution",
          text: `Image "${file.name}" does not have the required resolution of 1600x900. Please upload the correct resolution.`,
          icon: "error",
        });
        event.target.value = "";
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => {
    const validateForm = () => {
      const isHeaderImageSelected =
        formData.headerImage !== null &&
        (formData.headerImage.file ||
          formData.headerImage.preview ||
          typeof formData.headerImage === "string");

      const isCategorySelected = formData.categoryId !== "";
      const isNameFilled = formData.name.trim() !== "";
      const isDescriptionFilled = formData.description.trim() !== "";
      const isCustomUrlFilled = formData.customUrl.trim() !== "";

      const isValid =
        isHeaderImageSelected &&
        isCategorySelected &&
        isNameFilled &&
        isDescriptionFilled &&
        isCustomUrlFilled;

      setIsFormValid(isValid);
    };

    validateForm();
  }, [formData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formDataObj = new FormData();

      formDataObj.append("name", formData.name);
      formDataObj.append("description", formData.description);
      formDataObj.append("status", formData.status);
      formDataObj.append("categoryId", formData.categoryId);
      formDataObj.append("customUrl", formData.customUrl);
      if (formData.headerImage?.file) {
        formDataObj.append("headerImage", formData.headerImage.file);
      }

      // Prepare content for JSON and handle files
      const contentForJson = [];

      for (let index = 0; index < contentBlocks.length; index++) {
        const section = contentBlocks[index];

        // 1. Handle Text
        contentForJson.push({
          type: "text",
          content: section.text,
          alignment: section.textAlignment || "left",
        });

        // 2. Handle Image (if exists)
        if (section.image) {
          const imageIndex = contentForJson.length; // Next available index in contentForJson
          const imageBlock = {
            type: "image",
            content: "",
            alignment: section.imageAlignment || "right",
          };

          if (
            typeof section.image === "string" &&
            section.image.startsWith("data:")
          ) {
            const response = await fetch(section.image);
            const blob = await response.blob();
            const file = new File([blob], `image-${index}.jpg`, {
              type: "image/jpeg",
            });
            formDataObj.append(`content[${imageIndex}][content]`, file);
            imageBlock.content = ""; // Will be filled by backend
          } else if (section.image instanceof File) {
            formDataObj.append(`content[${imageIndex}][content]`, section.image);
            imageBlock.content = ""; // Will be filled by backend
          } else if (
            typeof section.image === "string" &&
            section.image.startsWith("http")
          ) {
            imageBlock.content = section.image;
          }
          contentForJson.push(imageBlock);
        }
      }

      formDataObj.append("content", JSON.stringify(contentForJson));

      let response;
      if (isEditMode) {
        response = await updateBlog(editingBlogId, formDataObj);
      } else {
        response = await postBlog(formDataObj);
      }

      resetForm();
      setIsModalOpen(false);

      loadData();

      Swal.fire({
        title: "Success!",
        text: isEditMode
          ? "Blog has been updated successfully."
          : "Blog has been created successfully.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });

      if (isEditMode) {
        navigate("/blogs");
      }
    } catch (error) {
      console.error("Error saving blog:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      Swal.fire({
        title: "Error!",
        text: isEditMode
          ? "Failed to update blog. Please try again."
          : "Failed to create blog. Please try again.",
        icon: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      categoryId: "",
      name: "",
      status: "draft",
      description: "",
      headerImage: null,
      customUrl: "",
    });
    setContentBlocks([{ text: "", textAlignment: "left", image: null, imageAlignment: "right" }]);
    setIsEditMode(false);
    setEditingBlogId(null);
    setIsFormValid(false);
  };

  const renderBlock = (section, index) => {
    return (
      <div
        key={index}
        className="mb-8 p-6 border border-gray-200 rounded-2xl bg-white relative shadow-sm hover:shadow-md transition-all border-l-4 border-l-blue-500"
      >
        <div className="flex flex-col gap-8">
          {/* Section Header */}
          <div className="flex justify-between items-center border-b pb-4">
            <span className="text-sm font-bold text-blue-600 uppercase tracking-widest">
              Section #{index + 1}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={(e) => addContentBlock(index, e)}
                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                title="Add Section Below"
              >
                <FiPlus className="w-5 h-5" />
              </button>
              {contentBlocks.length > 1 && (
                <button
                  type="button"
                  onClick={(e) => removeBlock(index, e)}
                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  title="Remove Section"
                >
                  <MdDelete className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>

          {/* Text Editor Area */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Text Content
              </label>
              <div className="flex gap-4 items-center bg-gray-50 p-2 rounded-lg">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-tighter">Alignment:</span>
                <label className="flex items-center gap-2 cursor-pointer group">
                  <input
                    type="radio"
                    name={`text-align-${index}`}
                    value="left"
                    checked={section.textAlignment === "left"}
                    onChange={() => handleAlignmentChange(index, "textAlignment", "left")}
                    className="w-3 h-3 text-blue-600"
                  />
                  <span className="text-xs group-hover:text-blue-600">Left</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer group">
                  <input
                    type="radio"
                    name={`text-align-${index}`}
                    value="right"
                    checked={section.textAlignment === "right"}
                    onChange={() => handleAlignmentChange(index, "textAlignment", "right")}
                    className="w-3 h-3 text-blue-600"
                  />
                  <span className="text-xs group-hover:text-blue-600">Right</span>
                </label>
              </div>
            </div>
            <div className="rich-text-editor">
              <ReactQuill
                value={section.text}
                onChange={(value) => handleTextChange(index, value)}
                modules={modules}
                formats={formats}
                placeholder="Write your section content here..."
                className="custom-quill"
              />
            </div>
          </div>

          {/* Image Upload Area */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Section Image (Optional)
              </label>
              <div className="flex gap-4 items-center bg-gray-50 p-2 rounded-lg">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-tighter">Alignment:</span>
                <label className="flex items-center gap-2 cursor-pointer group">
                  <input
                    type="radio"
                    name={`image-align-${index}`}
                    value="left"
                    checked={section.imageAlignment === "left"}
                    onChange={() => handleAlignmentChange(index, "imageAlignment", "left")}
                    className="w-3 h-3 text-green-600"
                  />
                  <span className="text-xs group-hover:text-green-600">Left</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer group">
                  <input
                    type="radio"
                    name={`image-align-${index}`}
                    value="right"
                    checked={section.imageAlignment === "right"}
                    onChange={() => handleAlignmentChange(index, "imageAlignment", "right")}
                    className="w-3 h-3 text-green-600"
                  />
                  <span className="text-xs group-hover:text-green-600">Right</span>
                </label>
              </div>
            </div>
            <div className="w-full">
              <input
                id={`section-image-upload-${index}`}
                type="file"
                className="sr-only"
                accept="image/*"
                onChange={(e) => handleImageUpload(index, e)}
              />
              {section.image ? (
                <div className="relative w-full h-[300px] group border-2 border-gray-100 rounded-xl overflow-hidden">
                  <img
                    src={normalizeImageUrl(section.image)}
                    alt="Section content"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-300 flex items-center justify-center gap-4 opacity-0 group-hover:opacity-100 z-10 pointer-events-none">
                    <label
                      htmlFor={`section-image-upload-${index}`}
                      className="p-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors transform scale-0 group-hover:scale-100 duration-300 shadow-xl cursor-pointer pointer-events-auto"
                      title="Change Image"
                    >
                      <FiEdit2 className="w-6 h-6" />
                    </label>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const newBlocks = [...contentBlocks];
                        newBlocks[index].image = null;
                        setContentBlocks(newBlocks);
                      }}
                      className="p-3 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors transform scale-0 group-hover:scale-100 duration-300 shadow-xl pointer-events-auto"
                      title="Remove Image"
                    >
                      <MdDelete className="w-6 h-6" />
                    </button>
                  </div>
                  {/* Invisible label overlay to make the whole image clickable to change */}
                  <label
                    htmlFor={`section-image-upload-${index}`}
                    className="absolute inset-0 cursor-pointer z-0"
                  />
                </div>
              ) : (
                <label
                  htmlFor={`section-image-upload-${index}`}
                  className="w-full min-h-[250px] flex flex-col justify-center items-center border-2 border-dashed border-gray-300 rounded-xl p-5 cursor-pointer bg-gray-50 hover:bg-blue-50 hover:border-blue-300 transition-all group"
                >
                  <div className="mx-auto h-16 w-16 text-gray-400 mb-4 bg-white rounded-full flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform">
                    <FiPlus className="w-8 h-8 text-blue-500" />
                  </div>
                  <div className="text-center">
                    <span className="block text-sm font-semibold text-blue-600 group-hover:text-blue-700">
                      Click anywhere in this section to upload image
                    </span>
                    <p className="text-xs text-gray-500 mt-2">
                      PNG, JPG, GIF up to 10MB
                    </p>
                  </div>
                </label>
              )}
            </div>
          </div>
        </div>

        <div className="mt-8 flex justify-center border-t pt-6">
          <button
            type="button"
            onClick={(e) => addContentBlock(index, e)}
            className="px-6 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-all flex items-center gap-2 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
          >
            <FiPlus className="w-5 h-5" />
            Add New Section
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Blogs</h2>
        <button
          onClick={() => setIsModalOpen(true)}
          className="btn btn-primary h-[44px]"
        >
          <FiPlus className="text-white" />
          Create New Blog
        </button>
      </div>

      <div className="bg-white p-5 w-full rounded-lg pagination-shadow flex flex-wrap xlCustom:flex-nowrap xlCustom:justify-end items-center gap-3 my-5">
        <div className="search-wrapper">
          <input
            type="text"
            className="search-input"
            placeholder="Search by blog name"
            value={nameQuery}
            onChange={(e) => setNameQuery(e.target.value)}
          />
          <FiSearch className="search-icon" />
        </div>
        <div className="search-wrapper">
          <input
            type="text"
            className="search-input"
            placeholder="Search by category name"
            value={categoryNameQuery}
            onChange={(e) => setCategoryNameQuery(e.target.value)}
          />
          <FiSearch className="search-icon" />
        </div>
        <select
          className="status-select"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
        >
          <option value="" className="opacity-25">
            Sort By
          </option>
          <option value="newest">Newest</option>
          <option value="oldest">Oldest</option>
          <option value="popular">Popular</option>
        </select>
        <div className="flex flex-wrap md:flex-nowrap gap-2">
          <button className="btn btn-filter filter-btn" onClick={handleSearch}>
            <FiFilter className="text-white" /> Apply Filter
          </button>
          <button
            className="btn btn-reset-filter filter-btn"
            onClick={handleReset}
          >
            <FiRotateCcw className="text-white" /> Reset Filter
          </button>
        </div>
      </div>

      {/* Blog Data Table */}
      <div className="bg-white w-full rounded-lg pagination-shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto bg-white rounded-lg  overflow-hidden">
            <thead>
              <tr className="brand-gradient text-white">
                <th className="table-th">Sr. No.</th>
                <th className="table-th">Name</th>
                <th className="table-th">Category Name</th>
                <th className="table-th">Status</th>
                <th className="table-th">Actions</th>
              </tr>
            </thead>
            <tbody>
              {blogs?.length > 0 ? (
                blogs?.map((blog, index) => (
                  <tr
                    key={blog.id}
                    className={`${index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
                      } hover:bg-gray-200 transition-colors duration-300`}
                  >
                    <td className="table-td">
                      <button
                        type="button"
                        onClick={() => handlePositionClick(blog)}
                        className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer font-medium"
                      >
                        {blog.position ?? "—"}
                      </button>
                    </td>
                    <td className="table-td">{blog.name}</td>
                    <td className="table-td">
                      {blog.category?.name || "Uncategorized"}
                    </td>
                    <td className="table-td">
                      <span
                        className={`px-2 py-1 rounded-full text-sm ${blog.status === "published"
                          ? "bg-green-200 text-green-800"
                          : "bg-yellow-200 text-yellow-800"
                          }`}
                      >
                        {blog.status === "published" ? "Published" : "Draft"}
                      </span>
                    </td>
                    <td className="table-td flex space-x-4">
                      <button
                        className="btn btn-view "
                        onClick={() => handleViewBlog(blog.id)}
                      >
                        <IoEye className="text-white" /> View
                      </button>
                      <button
                        className="btn btn-edit text-white"
                        onClick={() => handleEditBlog(blog.id)}
                      >
                        <FiEdit2 className="text-white" /> Edit
                      </button>
                      <button
                        className="btn btn-delete text-white"
                        onClick={() => handleDeleteBlog(blog.id)}
                      >
                        <MdDelete className="text-white" /> Delete
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr className="bg-gray-50 hover:bg-gray-100 transition-colors duration-300">
                  <td
                    colSpan={6}
                    className="px-6 py-4 text-center text-sm text-gray-700"
                  >
                    No blogs found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="mt-6">
          <GlobalPagination
            offset={pagination.offset}
            limit={pagination.limit}
            total={pagination.totalCount}
            onPageChange={handlePageChange}
            className=""
          />
        </div>
      )}

      {isPositionModalOpen && selectedBlogForPosition && (
        <RecordPosition
          isOpen={isPositionModalOpen}
          onClose={closePositionModal}
          currentPosition={selectedBlogForPosition.currentPosition}
          recordId={selectedBlogForPosition.recordId}
          onUpdatePosition={handleUpdatePosition}
          moduleName="Blog"
        />
      )}

      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            {isLoadingBlog ? (
              <div className="p-8 flex justify-center items-center min-h-[400px]">
                <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500"></div>
              </div>
            ) : (
              <div className="p-8">
                <div className="flex justify-between items-center mb-8">
                  <h2 className="text-3xl font-bold text-gray-800">
                    {isEditMode ? "Edit Blog" : "Create New Blog"}
                  </h2>
                  <button
                    onClick={() => {
                      setIsModalOpen(false);
                      if (isEditMode) {
                        resetForm();
                        navigate("/blogs");
                      }
                    }}
                    className="text-gray-500 hover:text-gray-700 transition-colors"
                    disabled={isSubmitting}
                  >
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-8">
                  {/* Header Image Section */}
                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Header Image <span className="text-red-500">*</span>
                    </label>
                    <div
                      className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-xl transition-colors ${formData.headerImage
                        ? "border-green-300 bg-green-50"
                        : "border-red-300 bg-red-50"
                        }`}
                    >
                      <div className="space-y-1 text-center">
                        {formData.headerImage ? (
                          <div className="relative group">
                            <img
                              src={normalizeImageUrl(
                                formData.headerImage.preview
                                  ? formData.headerImage.preview
                                  : formData.headerImage,
                              )}
                              alt="Header preview"
                              className="mx-auto h-48 w-full object-cover rounded-lg"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100">
                              <button
                                type="button"
                                onClick={() =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    headerImage: null,
                                  }))
                                }
                                className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors transform scale-0 group-hover:scale-100 duration-300"
                              >
                                <svg
                                  className="w-6 h-6"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M6 18L18 6M6 6l12 12"
                                  />
                                </svg>
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <svg
                              className="mx-auto h-16 w-16 text-gray-400"
                              stroke="currentColor"
                              fill="none"
                              viewBox="0 0 48 48"
                              aria-hidden="true"
                            >
                              <path
                                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                                strokeWidth={2}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            <div className="flex text-sm text-gray-600">
                              <label
                                htmlFor="header-image"
                                className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                              >
                                <span>Upload a header image</span>
                                <input
                                  id="header-image"
                                  name="header-image"
                                  type="file"
                                  className="sr-only"
                                  accept="image/*"
                                  onChange={handleHeaderImageUpload}
                                />
                              </label>
                              <p className="pl-1">or drag and drop</p>
                            </div>
                            <p className="text-xs text-gray-500">
                              PNG, JPG, GIF up to 10MB, 1600x900 pixels
                            </p>
                            {!formData.headerImage && (
                              <p className="text-xs text-red-500 mt-1">
                                Header image is required
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-lg font-medium text-gray-700 mb-2">
                        Category
                      </label>
                      <select
                        name="categoryId"
                        value={formData.categoryId}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select a category</option>
                        {Array.isArray(blogCategories) &&
                          blogCategories.map((category) => (
                            <option key={category.id} value={category.id}>
                              {category.name}
                            </option>
                          ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-lg font-medium text-gray-700 mb-2">
                        Name
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-lg font-medium text-gray-700 mb-2">
                        Status
                      </label>
                      <select
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="draft">Draft</option>
                        <option value="published">Published</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-lg font-medium text-gray-700 mb-2">
                        Custom URL
                      </label>
                      <input
                        type="text"
                        name="customUrl"
                        value={formData.customUrl}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[120px]"
                    />
                  </div>

                  <div className="border-t pt-8">
                    <h3 className="text-2xl font-medium text-gray-800 mb-6">
                      Content
                    </h3>
                    <div className="space-y-6">
                      {contentBlocks.map((block, index) =>
                        renderBlock(block, index),
                      )}
                    </div>
                  </div>

                  <div className="flex justify-end gap-4 pt-8 border-t">
                    <button
                      type="button"
                      onClick={() => {
                        setIsModalOpen(false);
                        if (isEditMode) {
                          resetForm();
                          navigate("/blogs");
                        }
                      }}
                      className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className={`px-6 py-3 rounded-lg transition-colors flex items-center gap-2 ${isSubmitting || !isFormValid
                        ? "bg-gray-400 opacity-50 cursor-not-allowed"
                        : "bg-blue-600 text-white hover:bg-blue-700"
                        }`}
                      disabled={isSubmitting || !isFormValid}
                    >
                      {isSubmitting ? (
                        <>
                          <svg
                            className="animate-spin h-5 w-5 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          {isEditMode ? "Updating..." : "Saving..."}
                        </>
                      ) : (
                        <>
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                          {isEditMode ? "Update Blog" : "Save Blog"}
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Blog;